/**
 * Authentication Provider for CHEC
 *
 * Wraps the application with MSAL authentication context.
 * Handles login redirect flow and provides authentication state.
 *
 * For local development:
 * - Set REACT_APP_API_X_USERNAME to bypass MSAL auth. The development proxy
 *   will generate a short-lived Bearer dev JWT containing this username and
 *   inject it into every proxied request automatically.
 * - Or register http://localhost:3000/chec/sso as SPA redirect URI in Azure AD
 */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser'
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react'
import { getMsalConfig, loginRequest, isInternalDomain } from './msalConfig'
import { getIsInteractiveAcquireTokenInProgress } from './useAccessToken'

// Feature flag: MSAL is only enabled when REACT_APP_ENABLE_MSAL=true
// This is set as a Docker build arg in the GHES workflow but NOT in the old ADO pipeline,
// ensuring old infrastructure deployments are unaffected
const isMsalEnabled = process.env.REACT_APP_ENABLE_MSAL === 'true'

// Check if auth should be bypassed for local development.
// When REACT_APP_API_X_USERNAME is set, skip MSAL — the development proxy
// generates a Bearer dev JWT from that username and injects it automatically.
const isLocalDevelopmentBypass =
  process.env.REACT_APP_API_X_USERNAME &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1')

// Plant domains use hostname-based auth (shared service account in API middleware).
// Matches both VIT (chec-plant.vit-*) and legacy AKS (chec-plant-*) patterns.
const isPlantDomain = /^chec-plant[.-]/.test(window.location.hostname)

// MSAL is only used when the feature flag is enabled, on internal domains (IAM),
// not in local development bypass mode, and not on plant domains
const shouldUseMsal =
  isMsalEnabled &&
  isInternalDomain() &&
  !isLocalDevelopmentBypass &&
  !isPlantDomain

// Create MSAL instance only for internal domains
const msalConfig = shouldUseMsal ? getMsalConfig() : null
const msalInstance = shouldUseMsal
  ? new PublicClientApplication(msalConfig)
  : null

// Handle redirect promise at module level - BEFORE React renders
// This is critical: MSAL needs to process the URL hash/query params immediately
let redirectHandledPromise = null

if (msalInstance) {
  console.log('[MSAL] Starting handleRedirectPromise at module load')
  redirectHandledPromise = msalInstance
    .handleRedirectPromise()
    .then((response) => {
      console.log('[MSAL] handleRedirectPromise completed:', {
        hasResponse: !!response,
        account: response?.account?.username,
      })
      return response
    })
    .catch((error) => {
      console.error('[MSAL] handleRedirectPromise error:', error)
      throw error
    })
}

/**
 * Inner component that handles authentication state
 * Only renders after redirect promise is resolved
 */
const AuthenticationHandler = ({ children }) => {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [isReady, setIsReady] = useState(false)
  const [loginTriggered, setLoginTriggered] = useState(false)

  // Wait for redirect promise to complete before proceeding
  useEffect(() => {
    if (redirectHandledPromise) {
      redirectHandledPromise
        .then(() => {
          console.log('[MSAL] Redirect handled, setting ready state')
          setIsReady(true)
        })
        .catch(() => {
          // Error already logged, still set ready to allow retry
          setIsReady(true)
        })
    } else {
      setIsReady(true)
    }
  }, [])

  // Log authentication state changes
  useEffect(() => {
    console.log('[MSAL] Auth state:', {
      isReady,
      isAuthenticated,
      inProgress,
      loginTriggered,
      accountCount: instance.getAllAccounts().length,
    })
  }, [isReady, isAuthenticated, inProgress, loginTriggered, instance])

  // Show loading while waiting for redirect to be processed
  if (!isReady) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <div>Processing authentication...</div>
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
          {isInternalDomain() ? 'IAM (Internal)' : 'CIAM (External)'}
        </div>
      </div>
    )
  }

  // Wait for any interactive MSAL interaction to complete.
  // Intentionally excludes SsoSilent — this is a background silent operation
  // that must not unmount the app.
  //
  // AcquireToken is ambiguous: MSAL uses InteractionStatus.AcquireToken for
  // BOTH silent (acquireTokenSilent) and interactive (acquireTokenRedirect)
  // token acquisition. Unmounting on every silent refresh was causing App to
  // re-initialise (re-fetch all baseline API calls + rebuild the SignalR
  // connection) every ~30 seconds. We therefore only treat AcquireToken as
  // interactive when useAccessToken has explicitly flagged that the redirect
  // fallback path is running.
  const interactiveInProgress =
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.Logout ||
    inProgress === InteractionStatus.HandleRedirect ||
    (inProgress === InteractionStatus.AcquireToken &&
      getIsInteractiveAcquireTokenInProgress())

  if (interactiveInProgress) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Authenticating... ({inProgress})</div>
      </div>
    )
  }

  // If not authenticated after redirect is handled, trigger login ONCE
  if (!isAuthenticated && !loginTriggered) {
    console.log('[MSAL] Not authenticated, triggering loginRedirect')
    setLoginTriggered(true)

    // Use setTimeout to avoid calling loginRedirect during render
    setTimeout(() => {
      instance.loginRedirect(loginRequest).catch((error) => {
        console.error('[MSAL] Login redirect error:', error)
      })
    }, 100)

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Redirecting to login...</div>
      </div>
    )
  }

  // If login was triggered but still not authenticated, show waiting state
  if (!isAuthenticated && loginTriggered) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Waiting for authentication...</div>
      </div>
    )
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          Redirecting to login...
        </div>
      </UnauthenticatedTemplate>
    </>
  )
}

AuthenticationHandler.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * Main Auth Provider component
 */
const AuthProvider = ({ children }) => {
  // Skip MSAL for:
  // 1. Local development with dev-JWT bypass (REACT_APP_API_X_USERNAME set)
  // 2. External domains (CIAM) - oauth2-proxy-ciam handles auth at infrastructure level
  if (!shouldUseMsal) {
    return children
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticationHandler>{children}</AuthenticationHandler>
    </MsalProvider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export { msalInstance }
export default AuthProvider
